<template>
  <section class="homepage-search-banner" :class="{ desktop: $isDesktop }">
    <img
      v-if="!$isDesktop"
      src="@/assets/images/pitchup-logo.png"
      class="homepage-logo"
      alt=""
      width="74"
      height="64"
    />
    <div class="search-banner-wrapper">
      <div>
        <translate class="heading strapline">
          Make your great escape
        </translate>
        <translate
          tag="h1"
          class="subhead"
          data-desktop-location-scroll-target
          :tt="{ bookableCountriesCount: '67' }"
        >
          Book campsites, glamping sites and holiday parks in
          {bookableCountriesCount} countries.
        </translate>
      </div>

      <div class="search-wizard-homepage-banner">
        <pu-search-wizard-link v-if="!$isDesktop" class="banner-link">
          <search-wizard-homepage-banner-categories
            :category-ids="categoryIds"
          />
        </pu-search-wizard-link>
        <search-bar-desktop
          v-if="$isDesktop"
          :key="`search-bar-${$route.name}`"
          class="homepage-search-input desktop"
        />
        <div v-else>
          <campsites-search-wizard-main class="homepage-search-input" />
        </div>

        <pu-search-wizard-link v-if="!$isDesktop" class="banner-link options">
          <search-wizard-homepage-banner-options
            :party="party"
            :dates="dates"
          />
        </pu-search-wizard-link>
        <pu-search-link
          v-if="!$isDesktop"
          @click="sendGAEvent"
          category="maps"
          class="map-search-link"
        >
          <img
            src="~/assets/images/map.svg"
            alt="$t('Map')"
            width="21"
            height="18"
          />
          <translate>Search using the map</translate>
        </pu-search-link>
      </div>

      <pu-search-link
        v-if="$isDesktop"
        @click="onSearchUsingMapDesktopClick"
        category="search"
        class="map-search-link"
      >
        <img
          src="~/assets/images/map.svg"
          alt="$t('Map')"
          width="21"
          height="18"
        />
        <translate>Search using the map</translate>
      </pu-search-link>
      <div id="hero7" class="hero" />
      <div id="hero6" class="hero" />
      <div id="hero5" class="hero" />
      <div id="hero4" class="hero" />
      <div id="hero3" class="hero" />
      <div id="hero2" class="hero" />
      <div id="hero1" class="hero" />
    </div>

    <homepage-background class="homepage-background" />
    <div class="search-banner-bottom" />
  </section>
</template>

<script setup lang="ts">
import { routeToFilters } from '../../../pu-links/infrastructure/routeToFilters'
import backgroundImageUrl from '~/assets/homepage/hero/1.jpg?url'

defineEmits(['wizardOpened'])

const { $isDesktop } = useNuxtApp()
const searchWizardStore = useSearchWizardStore(usePinia())
const searchStore = useSearchStore(usePinia())
searchWizardStore.openOnFocus = true

const categoryIds = computed(() => {
  return filters.value.categoryIds || []
})

const party = computed(() => {
  return filters.value.party || ''
})

const dates = computed(() => {
  return filters.value.dates || ''
})

const filters = computed(() => {
  const route = useRoute()
  return routeToFilters(route)
})

function sendGAEvent() {
  const { $gtm } = useNuxtApp()
  $gtm.push({ event: 'map_search_homepage_cta' })
}

function onSearchUsingMapDesktopClick() {
  sendGAEvent()
  if (!searchStore.showMap) {
    searchStore.toggleMap(true)
  }
}

watch(
  searchWizardStore.wizardFilters,
  async () => {
    await searchWizardStore.updateCounts()
  },
  { deep: true },
)

if ($isDesktop) {
  useHead({
    link: [
      {
        rel: 'preload',
        as: 'image',
        href: backgroundImageUrl,
        fetchpriority: 'high',
      },
    ],
  })
}
</script>

<style>
.homepage-search-banner .accommodation-wizard,
.homepage-search-banner .dates-wizard,
.homepage-search-banner .guests-wizard,
.homepage-search-banner .campsite-search-wizard-main-actions,
.homepage-search-banner .location-section .help-text,
.homepage-search-banner .location-section .location-options {
  display: none;
}

.homepage-search-banner .search-banner-wrapper .homepage-search-input {
  margin: 0 0 0.5em 0;
  width: auto;
}

.homepage-search-open
  .homepage-search-banner
  .search-banner-wrapper
  .homepage-search-input {
  margin: 0 0 0 0;
}

.search-banner-wrapper .homepage-search-input .search-form {
  padding: 0;
  background: none;
}

.homepage.homepage-search-open .search-banner-wrapper .search-form {
  padding: 1.4em;
  background: linear-gradient(white, #f1f0f4 15%);
  height: 100dvh;
}

.homepage.homepage-search-open .accommodation-wizard,
.homepage.homepage-search-open .dates-wizard,
.homepage.homepage-search-open .guests-wizard,
.homepage.homepage-search-open .campsite-search-wizard-main-actions,
.homepage.homepage-search-open .location-section .help-text,
.homepage.homepage-search-open .location-section .location-options {
  display: block;
}

.homepage.homepage-search-open .homepage-background,
.homepage.homepage-search-open .homepage-logo,
.homepage.homepage-search-open .banner-link,
.homepage.homepage-search-open .map-search-link,
.homepage.homepage-search-open .social-proof,
.homepage.homepage-search-open .recently-viewed-campsites,
.homepage.homepage-search-open .app-footer {
  display: none;
}
</style>

<style scoped>
.homepage-search-banner {
  position: relative;
  width: 100%;
}

.search-banner-wrapper {
  padding: 0 1.4em;
  margin: 3em 0 0 0;
  background-image: linear-gradient(90deg, #238338, #0a9c2b);
}

.homepage-search-open .search-banner-wrapper {
  margin-top: 0;
  padding: 0;
  position: relative;
}

.strapline.heading {
  color: white;
  font-size: 1.4em;
  margin: 0 0 0.5em 0;
  display: block;
  line-height: 1;
}

.subhead {
  color: white;
  font-size: var(--body);
  font-weight: normal;
  line-height: 1.25em;
  margin: 0 0 1em;
}

.homepage-logo {
  height: auto;
  left: 14vw;
  margin: 0;
  position: absolute;
  top: -12vw;
  width: 4.65em;
}

/* mobile landscape */
@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  .homepage-logo {
    top: -3em;
    width: 4.25em;
  }
}

.search-wizard-homepage-banner {
  background: #fff;
  box-shadow: 0 0.05em 1em rgb(20 44 107 / 50%);
  border-radius: 0.65em;
  padding: 0.75em 1em;
  min-height: 9em;
  text-decoration: none;
  display: block;
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.homepage-search-open .search-wizard-homepage-banner {
  width: 100%;
  padding: 0;
  position: absolute;
  top: 0;
  box-shadow: none;
  z-index: unset;
  overflow: unset;
}

.homepage-search-input.wizard-open {
  margin-bottom: 0;
  position: absolute;
  top: 0;
}

.banner-link {
  text-decoration: none;
  order: 2;
}

.search-banner-bottom {
  background: white;
  height: 2em;
  margin: -1em 0 0 0;
  z-index: -1;
}

.map-search-link {
  background-color: #f0f2f5;
  margin: 0.5em -1em -0.75em;
  padding: 0.6em 1em;
  color: var(--colour-wcag-blue);
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35em;
  line-height: 1;
}

.map-search-link img {
  opacity: 0.75;
  filter: saturate(0.5) hue-rotate(97deg);
}

.map-search-link:active {
  background-color: var(--colour-page-background);
}

/* desktop & tablet */
.desktop .homepage-logo {
  width: 9em;
  top: -9em;
  left: 5em;
}

.desktop .search-banner-bottom {
  background: none;
  height: auto;
  margin: 0;
}

.desktop .search-banner-wrapper {
  padding: 1em 5em;
  margin: 0;
  min-height: 28em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  position: relative;
  background: transparent;
  row-gap: 1em;
}

.desktop .search-wizard-homepage-banner {
  align-items: center;
  background: rgb(171 202 232 / 85%);
  border-radius: 3em;
  box-shadow: none;
  display: flex;
  justify-content: flex-start;
  min-height: 5em;
  padding: 1em;
  width: auto;
  z-index: 2;
  overflow: unset;
}

.homepage-search-banner.desktop .map-search-link {
  background-color: rgba(255, 255, 255, 0.9);
  color: var(--colour-dark-blue);
  z-index: 1;
  border-radius: 3em;
  font-weight: 500;
  box-shadow: 0 0.05em 1em rgb(0 0 0 / 50%);
  gap: 0.6em;
  padding: 0.5em 1.2em;
  margin: 0;
  border: 3px solid transparent;
  transition:
    border 0.15s ease-out,
    background-color 0.2s ease-out;
}

.homepage-search-banner.desktop .map-search-link img {
  opacity: 1;
  filter: saturate(0.65) hue-rotate(97deg) brightness(0.8);
  width: 1.5em;
}

.homepage-search-banner.desktop .map-search-link:hover,
.homepage-search-banner.desktop .map-search-link:focus-visible,
.homepage-search-banner.desktop .map-search-link:active {
  border-color: var(--colour-dark-blue);
  background-color: var(--colour-page-background-light);
  outline: none;
}

.desktop .homepage-search-input.desktop {
  width: 100%;
  margin: 0;
}

.desktop .heading {
  text-align: center;
  font-size: 3em;
  font-weight: bold;
  margin: 0;
  padding: 1em 0;
  position: relative;
  z-index: 1;
}

.desktop .heading.strapline {
  text-shadow: 0px 3px 8px rgb(0 0 0 / 50%);
  font-size: 4em;
  padding: 0;
}

.desktop .subhead {
  text-shadow: 0px 3px 8px rgb(0 0 0 / 50%);
  font-size: 1.8em;
  text-align: center;
  margin: 1em auto;
  max-width: 30ch;
  position: relative;
  z-index: 1;
  text-wrap: balance;
}

.desktop .location {
  order: -1;
}

.desktop .banner-link.options {
  flex-grow: 1;
}

.desktop .homepage-background {
  display: none;
}

.desktop .hero {
  animation: fade 140s infinite;
  background-color: var(--colour-blue);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: block;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

.desktop #hero1 {
  animation-delay: 0s;
  background-image: url('~/assets/homepage/hero/1.jpg');
}

.desktop #hero2 {
  background-image: url('~/assets/homepage/hero/2.jpg');
  animation-delay: 20s;
  filter: brightness(0.7);
}

.desktop #hero3 {
  background-image: url('~/assets/homepage/hero/3.jpg');
  animation-delay: 40s;
}

.desktop #hero4 {
  background-image: url('~/assets/homepage/hero/4.jpg');
  animation-delay: 60s;
}

.desktop #hero5 {
  background-image: url('~/assets/homepage/hero/5.jpg');
  animation-delay: 80s;
  filter: brightness(0.8);
}

.desktop #hero6 {
  background-image: url('~/assets/homepage/hero/6.jpg');
  animation-delay: 100s;
}

.desktop #hero7 {
  background-image: url('~/assets/homepage/hero/7.jpg');
  animation-delay: 120s;
  filter: brightness(0.8);
}

/* https://www.devtwins.com/blog/css-cross-fading-images */
@keyframes fade {
  0% {
    opacity: 1;
  }

  12.85% {
    opacity: 1;
  }

  14% {
    opacity: 0;
  }

  98.5% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@media (min-width: 1606px) {
  .desktop .hero {
    border-radius: 1em 1em 0 0;
  }
}
</style>
